import { useCallback, useEffect, useState } from 'react';
import { Landmark, Link2, Loader2, Plus } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../lib/contextoAuth';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';
import Motivo from '../experiencia/Motivo';
import { pesos } from './dinero';

/* =====================================================================
   Bancos y conciliación · ERP-OPERATIVO EF3
   =====================================================================
   Las cuentas de banco de la empresa (alias, banco, últimos cuatro),
   lo que entra y sale de cada una, y las dos listas de «sin conciliar».
   Un movimiento es un hecho: no se edita; se concilia con otro.
   ===================================================================== */
export default function Bancos() {
  const { puede } = useAuth();
  const avisos = useAvisos();
  const [cuentas, setCuentas] = useState(null);
  const [movs, setMovs] = useState([]);
  const [pendientes, setPendientes] = useState({ cobros_y_pagos_sin_banco: [], movimientos_sin_hecho: [] });
  const [error, setError] = useState('');
  const [alta, setAlta] = useState(false);
  const [nuevo, setNuevo] = useState(false);
  const [conciliando, setConciliando] = useState(null);

  // Se recarga subiendo un contador: el efecto lee y guarda cuando llega
  // la respuesta, nunca de forma síncrona (regla del compilador de React).
  const [tick, setTick] = useState(0);
  const cargar = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get('/bancos/cuentas'), api.get('/bancos/movimientos'), api.get('/bancos/sin-conciliar'),
    ]).then(([c, m, p]) => {
      if (!vivo) return;
      setCuentas(c); setMovs(m); setPendientes(p); setError('');
    }).catch((e) => { if (vivo) setError(e.message); });
    return () => { vivo = false; };
  }, [tick]);

  if (error && !cuentas) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!cuentas) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo el banco…
      </p>
    );
  }
  const activas = cuentas.filter((c) => c.activo);

  return (
    <div className="flex max-w-5xl flex-col gap-5 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Bancos</h1>
        <p className="text-gray-600">Tus cuentas de banco, lo que entra y sale, y lo que falta por conciliar.</p>
        <Ayuda id="finanzas.bancos.pantalla" />
      </header>

      <section className="rounded-xl border border-gray-200">
        <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2">
          <h2 className="font-semibold text-gray-800">Cuentas</h2>
          {puede('bancos.registrar') && (
            <button type="button" onClick={() => setAlta((v) => !v)}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-marca)]">
              <Plus className="h-4 w-4" aria-hidden="true" /> Nueva cuenta
            </button>
          )}
        </div>
        {alta && <FormularioCuenta onHecho={(m) => { avisos.exito(m); setAlta(false); cargar(); }} />}
        {activas.length === 0 ? (
          <NotaExplicativa titulo="Todavía no hay cuentas de banco">
            Da de alta la cuenta en la que cobras y desde la que pagas. TEGO solo guarda el alias, el banco y los últimos cuatro dígitos.
          </NotaExplicativa>
        ) : (
          <ul className="divide-y divide-gray-100">
            {activas.map((c) => (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 px-3 py-2 text-sm">
                <Landmark className="h-4 w-4 text-gray-400" aria-hidden="true" />
                <span className="font-medium text-gray-900">{c.alias}</span>
                <span className="text-gray-500">{c.banco}{c.ultimos_cuatro ? ` · ····${c.ultimos_cuatro}` : ''}</span>
                <span className="ml-auto tabular-nums font-semibold">{pesos(c.saldo)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {puede('bancos.registrar') && activas.length > 0 && (
        <>
          <button type="button" onClick={() => setNuevo((v) => !v)}
                  className="inline-flex w-fit items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white">
            <Plus className="h-4 w-4" aria-hidden="true" /> Registrar movimiento
          </button>
          {nuevo && <FormularioMovimiento cuentas={activas} pendientes={pendientes.cobros_y_pagos_sin_banco}
                                          onHecho={(m) => { avisos.exito(m); setNuevo(false); cargar(); }} />}
        </>
      )}

      <section className="rounded-xl border border-amber-200">
        <h2 className="border-b border-amber-200 px-3 py-2 font-semibold text-amber-900">
          Sin conciliar
        </h2>
        <div className="p-3"><Ayuda id="finanzas.bancos.sin_conciliar" /></div>
        <div className="grid gap-3 p-3 sm:grid-cols-2">
          <div>
            <h3 className="text-xs font-semibold uppercase text-gray-500">Cobros y pagos que el banco no nombra ({pendientes.cobros_y_pagos_sin_banco.length})</h3>
            <ul className="divide-y divide-gray-100 text-sm">
              {pendientes.cobros_y_pagos_sin_banco.map((h) => (
                <li key={`${h.tipo}-${h.id}`} className="flex gap-2 py-1">
                  <span className="text-gray-500">{h.tipo === 'COBRO' ? 'Cobro' : 'Pago'} {h.folio}</span>
                  <span className="flex-1">{h.contraparte}</span>
                  <span className="tabular-nums">{pesos(h.importe)}</span>
                </li>
              ))}
              {pendientes.cobros_y_pagos_sin_banco.length === 0 && <li className="py-1 text-gray-500">Nada pendiente.</li>}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase text-gray-500">Movimientos del banco sin cobro ni pago ({pendientes.movimientos_sin_hecho.length})</h3>
            <ul className="divide-y divide-gray-100 text-sm">
              {pendientes.movimientos_sin_hecho.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center gap-2 py-1">
                  <span className="text-gray-500">{m.fecha}</span>
                  <span className="flex-1">{m.concepto}</span>
                  <span className="tabular-nums">{pesos(m.importe)}</span>
                  {puede('bancos.registrar') && (
                    <button type="button" onClick={() => setConciliando(conciliando?.id === m.id ? null : m)}
                            className="inline-flex items-center gap-1 font-semibold text-[var(--color-marca)]">
                      <Link2 className="h-3 w-3" aria-hidden="true" /> Conciliar
                    </button>
                  )}
                </li>
              ))}
              {pendientes.movimientos_sin_hecho.length === 0 && <li className="py-1 text-gray-500">Nada pendiente.</li>}
            </ul>
          </div>
        </div>
        {conciliando && (
          <Conciliar movimiento={conciliando} hechos={pendientes.cobros_y_pagos_sin_banco}
                     onHecho={(m) => { avisos.exito(m); setConciliando(null); cargar(); }} />
        )}
      </section>

      <section className="rounded-xl border border-gray-200">
        <h2 className="border-b border-gray-200 px-3 py-2 font-semibold text-gray-800">Últimos movimientos</h2>
        <ul className="divide-y divide-gray-100 text-sm">
          {movs.slice(0, 50).map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-x-3 px-3 py-1.5">
              <span className="text-gray-500">{m.fecha}</span>
              <span className="text-gray-500">{m.alias}</span>
              <span className="flex-1 text-gray-900">{m.concepto}</span>
              {m.conciliado_tipo && <span className="text-xs text-green-700">conciliado</span>}
              <span className={`tabular-nums ${Number(m.importe) < 0 ? 'text-red-700' : 'text-green-700'}`}>{pesos(m.importe)}</span>
            </li>
          ))}
          {movs.length === 0 && <li className="px-3 py-2 text-gray-500">Sin movimientos.</li>}
        </ul>
      </section>
    </div>
  );
}

function FormularioCuenta({ onHecho }) {
  const [f, setF] = useState({ alias: '', banco: '', ultimos_cuatro: '', saldo_inicial: '0' });
  const [error, setError] = useState('');
  const listo = f.alias.trim().length >= 2 && f.banco.trim().length >= 2
    && (!f.ultimos_cuatro || /^\d{4}$/.test(f.ultimos_cuatro));
  async function guardar(e) {
    e.preventDefault();
    try {
      const r = await api.post('/bancos/cuentas', {
        alias: f.alias.trim(), banco: f.banco.trim(), ultimos_cuatro: f.ultimos_cuatro || null,
        saldo_inicial: Number(f.saldo_inicial || 0).toFixed(2),
      });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); }
  }
  return (
    <form onSubmit={guardar} className="grid gap-3 border-b border-gray-200 p-3 sm:grid-cols-2">
      <label className="flex flex-col text-sm">Alias *
        <input value={f.alias} onChange={(e) => setF({ ...f, alias: e.target.value })} className="rounded-lg border border-gray-300 p-2" placeholder="BBVA nómina" />
      </label>
      <label className="flex flex-col text-sm">Banco *
        <input value={f.banco} onChange={(e) => setF({ ...f, banco: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">Últimos cuatro
        <input value={f.ultimos_cuatro} maxLength={4} onChange={(e) => setF({ ...f, ultimos_cuatro: e.target.value.replace(/\D/g, '') })} className="rounded-lg border border-gray-300 p-2" />
        <Ayuda id="finanzas.bancos.ultimos_cuatro" />
      </label>
      <label className="flex flex-col text-sm">Saldo inicial
        <input type="number" step="0.01" value={f.saldo_inicial} onChange={(e) => setF({ ...f, saldo_inicial: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      {error && <p className="text-sm text-red-700 sm:col-span-2">{error}</p>}
      <button type="submit" disabled={!listo} aria-describedby={listo ? undefined : 'motivo-cuenta'} className="w-fit rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">Guardar cuenta</button>
      {/* BOT-1 (19 sep 2026): por qué está gris, a la vista. */}
      <Motivo id="motivo-cuenta" cuando={!listo}>
        {f.ultimos_cuatro && !/^\d{4}$/.test(f.ultimos_cuatro) ? 'Los últimos cuatro dígitos son exactamente cuatro números.' : 'Falta el alias y el banco (mínimo 2 letras cada uno).'}
      </Motivo>
    </form>
  );
}

function FormularioMovimiento({ cuentas, pendientes, onHecho }) {
  const [f, setF] = useState({ id_cuenta_bancaria: cuentas[0]?.id || '', importe: '', concepto: '', referencia: '', hecho: '' });
  const [error, setError] = useState('');
  const listo = f.id_cuenta_bancaria && Number(f.importe) !== 0 && f.concepto.trim().length >= 2;
  async function guardar(e) {
    e.preventDefault();
    try {
      const [tipo, id] = f.hecho ? f.hecho.split(':') : [null, null];
      const r = await api.post('/bancos/movimientos', {
        id_cuenta_bancaria: Number(f.id_cuenta_bancaria), importe: Number(f.importe).toFixed(2),
        concepto: f.concepto.trim(), referencia: f.referencia || null,
        conciliado_tipo: tipo, conciliado_id: id ? Number(id) : null,
        clave_idempotencia: crypto.randomUUID(),
      });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); }
  }
  return (
    <form onSubmit={guardar} className="grid gap-3 rounded-xl border border-gray-200 p-3 sm:grid-cols-2">
      <label className="flex flex-col text-sm">Cuenta
        <select value={f.id_cuenta_bancaria} onChange={(e) => setF({ ...f, id_cuenta_bancaria: e.target.value })} className="rounded-lg border border-gray-300 p-2">
          {cuentas.map((c) => <option key={c.id} value={c.id}>{c.alias}</option>)}
        </select>
      </label>
      <label className="flex flex-col text-sm">Importe (negativo si sale) *
        <input type="number" step="0.01" value={f.importe} onChange={(e) => setF({ ...f, importe: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">Concepto *
        <input value={f.concepto} onChange={(e) => setF({ ...f, concepto: e.target.value })} className="rounded-lg border border-gray-300 p-2" />
      </label>
      <label className="flex flex-col text-sm">Corresponde a
        <select value={f.hecho} onChange={(e) => {
          const h = pendientes.find((x) => `${x.tipo}:${x.id}` === e.target.value);
          setF({ ...f, hecho: e.target.value, importe: h ? String(h.importe) : f.importe });
        }} className="rounded-lg border border-gray-300 p-2">
          <option value="">Sin decidir</option>
          {pendientes.map((h) => (
            <option key={`${h.tipo}:${h.id}`} value={`${h.tipo}:${h.id}`}>
              {h.tipo === 'COBRO' ? 'Cobro' : 'Pago'} {h.folio} · {h.contraparte} · {pesos(h.importe)}
            </option>
          ))}
        </select>
      </label>
      {error && <p className="text-sm text-red-700 sm:col-span-2">{error}</p>}
      <button type="submit" disabled={!listo} aria-describedby={listo ? undefined : 'motivo-movimiento'} className="w-fit rounded-xl bg-[var(--color-marca)] px-4 py-2 font-semibold text-white disabled:bg-gray-300">Registrar</button>
      <Motivo id="motivo-movimiento" cuando={!listo}>Elige la cuenta, pon un importe distinto de cero y un concepto (mínimo 2 letras).</Motivo>
    </form>
  );
}

function Conciliar({ movimiento, hechos, onHecho }) {
  const candidatos = hechos.filter((h) => Number(h.importe) === Number(movimiento.importe));
  const [elegido, setElegido] = useState(candidatos[0] ? `${candidatos[0].tipo}:${candidatos[0].id}` : '');
  const [error, setError] = useState('');
  async function unir() {
    const [tipo, id] = elegido.split(':');
    try {
      const r = await api.post(`/bancos/movimientos/${movimiento.id}/conciliar`, { tipo, id: Number(id) });
      onHecho(r.mensaje);
    } catch (err) { setError(err.message); }
  }
  return (
    <div className="flex flex-wrap items-center gap-3 border-t border-amber-200 p-3 text-sm">
      <span>{movimiento.concepto} · {pesos(movimiento.importe)} corresponde a:</span>
      {candidatos.length === 0 ? (
        <span className="text-amber-800">No hay ningún cobro ni pago pendiente por ese importe. Regístralo primero, o registra el movimiento contrario con su concepto.</span>
      ) : (
        <>
          <select value={elegido} onChange={(e) => setElegido(e.target.value)} className="rounded-lg border border-gray-300 p-2">
            {candidatos.map((h) => (
              <option key={`${h.tipo}:${h.id}`} value={`${h.tipo}:${h.id}`}>
                {h.tipo === 'COBRO' ? 'Cobro' : 'Pago'} {h.folio} · {h.contraparte}
              </option>
            ))}
          </select>
          <button type="button" onClick={unir} className="rounded-xl bg-[var(--color-marca)] px-3 py-1.5 font-semibold text-white">Unir</button>
        </>
      )}
      {error && <span className="text-red-700">{error}</span>}
    </div>
  );
}
